import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Save } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SuiteButton } from "@/components/suite";
import { MONTHS_IT } from "@/lib/constants";

export const COUNTER_LABELS = { FERIE: "Ferie", ROL: "ROL / Permessi", EX_FEST: "Ex festività" };
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const empty = { counter_type: "ROL", quantity: "", origin: "", reason: "" };

// mode: "create" | { reverse: entry }
export function CounterAdjustmentDialog({ employeeId, info, mode, onClose, onSaved }) {
  const [form, setForm] = useState(empty);
  const [key, setKey] = useState(newKey);
  const [busy, setBusy] = useState(false);
  const reverse = mode && mode !== "create" ? mode.reverse : null;

  useEffect(() => { if (mode) { setForm(empty); setKey(newKey()); } }, [mode]);

  const unit = form.counter_type === "FERIE" ? info?.vacation_unit : "hours";
  const booking = info?.first_open_month;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      if (reverse) {
        await api.post(`/counters-consolidation/employees/${employeeId}/adjustments/${reverse.id}/reverse`,
          { reason: form.reason, idempotency_key: key });
        toast.success("Storno registrato; la rettifica originale resta nello storico");
      } else {
        const [oy, om] = form.origin ? form.origin.split("-").map(Number) : [null, null];
        await api.post(`/counters-consolidation/employees/${employeeId}/adjustments`, {
          counter_type: form.counter_type, quantity: Number(String(form.quantity).replace(",", ".")),
          unit, reason: form.reason, idempotency_key: key,
          origin_year: oy, origin_month: om, booking_year: booking?.year, booking_month: booking?.month,
        });
        toast.success("Rettifica registrata e tracciata nell'audit");
      }
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!mode} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="counter-adjustment-dialog">
        <DialogHeader>
          <DialogTitle>{reverse ? "Storna rettifica" : "Nuova rettifica Contatori"}</DialogTitle>
          <DialogDescription className="text-xs">
            Registrazione nel primo mese aperto: <b data-testid="adjustment-booking-month">{booking ? `${MONTHS_IT[booking.month - 1]} ${booking.year}` : "—"}</b>.
            Modifica solo il saldo residuo: presenze, Cartellino, banca ore e paghe restano invariati.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {reverse ? (
            <div className="text-xs text-suite-neutral-700" data-testid="adjustment-reverse-summary">
              {COUNTER_LABELS[reverse.counter_type]} · quantità {reverse.quantity} {reverse.unit === "days" ? "gg" : "h"} → storno {-reverse.quantity}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs space-y-1">Contatore
                <select value={form.counter_type} onChange={set("counter_type")} data-testid="adjustment-counter-select"
                  className="w-full h-9 rounded-md border border-suite-neutral-300 px-2 text-sm">
                  {Object.entries(COUNTER_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <label className="text-xs space-y-1">Quantità (+/−) in {unit === "days" ? "giorni" : "ore"}
                <Input value={form.quantity} onChange={set("quantity")} inputMode="decimal" placeholder="es. -1,5" data-testid="adjustment-quantity-input" />
              </label>
              <label className="text-xs space-y-1 col-span-2">Competenza originaria dell'errore (facoltativa)
                <Input type="month" value={form.origin} onChange={set("origin")} data-testid="adjustment-origin-input" />
              </label>
            </div>
          )}
          <label className="text-xs space-y-1 block">Motivazione (obbligatoria)
            <Textarea value={form.reason} onChange={set("reason")} rows={3} data-testid="adjustment-reason-input" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="adjustment-cancel-btn"><X size={14} className="mr-1" /> Annulla</Button>
          <SuiteButton onClick={save} icon={Save} loading={busy} disabled={form.reason.trim().length < 3 || (!reverse && !form.quantity)}
            data-testid="adjustment-save-btn">{reverse ? "Conferma storno" : "Registra rettifica"}</SuiteButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
